import { Link } from "react-router";
import { EntityData } from "@api/internal/utils/types";
import { FeedSectionItems } from "@pages/Section/utils/types";
import { defaultTimestampFormat } from "@util/constant";
import { cn, getFormattedTime } from "@util/helpers";

export type ArticlesFromSameSectionProps = {
  /** Data containing articles from the same section or keywords.*/
  data?: FeedSectionItems[];
  /** Entity data containing section or keyword information. */
  entity?: EntityData;
  /** URL parameters to append to the article links. */
  linkParams?: URLSearchParams;
  /** Current article ID to exclude from the list. */
  currentArticleId?: string;
};

/**
 * Renders a list of articles from the same section or keywords as the current article.
 */
const ArticlesFromSameSection = ({
  data = [],
  entity,
  linkParams,
  currentArticleId,
}: ArticlesFromSameSectionProps): React.ReactElement => {
  if (data.length === 0 || !entity) return <></>;

  const isSection = "sections" in entity && Array.isArray(entity.sections);

  const dataEntity = isSection ? entity.sections?.[0] : entity.tag;

  if (!dataEntity) return <></>;

  const name = dataEntity?.name;
  const urlPath = dataEntity?.urlPath;

  const params = linkParams ? `?${linkParams?.toString()}` : "";

  const filteredData = data.filter(
    (item) => item.articleData.id !== currentArticleId
  );

  return (
    <section
      className="my-5"
      data-testid="articles-from-same-section-component"
    >
      <h2 className="border-t-2 border-gray-850 pt-3 font-poppins text-lg font-semibold uppercase tracking-[0.05625rem]">
        <Link
          className="!text-gray-850"
          to={`${urlPath}${params}`}
          reloadDocument
        >
          more in {name}
        </Link>
      </h2>

      <ul className="grid lg:grid-cols-2">
        {filteredData.slice(0, 8).map(({ articleData }, index) => {
          const { id, title, publishTime, urlPath } = articleData;

          return (
            <li
              key={id}
              className="h-full border-b border-gray-175 py-3 text-gray-850"
            >
              <div
                className={cn(
                  "mb-2 mt-1 flex h-full flex-grow flex-col border-gray-175 py-3 pb-2",
                  {
                    "lg:border-r lg:pr-3": [0, 2, 4, 6, 8].includes(index),
                    "lg:pl-3": [1, 3, 5, 7].includes(index),
                  }
                )}
              >
                <h2 className="font-lct text-base font-bold">
                  <Link
                    className="block !text-gray-850"
                    to={`${urlPath}${params}`}
                    reloadDocument
                  >
                    {title}
                  </Link>
                </h2>

                <p className="mb-0 mt-auto font-poppins text-4xs uppercase text-gray-550">
                  {getFormattedTime(publishTime, defaultTimestampFormat)}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default ArticlesFromSameSection;
